/**
 * CAP-40007 TCL-2727 - PingFederate OIDC authentication service.
 * Replaces the legacy LDAP login: TCL-GUI is now an OIDC client that authenticates the
 * user directly on the PingFederate login page using the Authorization Code flow + PKCE.
 * Wraps angular-oauth2-oidc to configure the IdP, run the login/redirect handshake,
 * expose the access token (for the HTTP interceptor) and the user identity/roles.
 *
 * Lifecycle (CAP-40007 TCL-2727):
 *   1. configure()            – called from APP_INITIALIZER (before router activates)
 *   2. init()                 – called from AppComponent.ngOnInit (discovery + token exchange)
 *   3. isDoneLoading$ / login – consumed by AuthGuardService (async guard)
 */
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService, AuthConfig, OAuthErrorEvent } from 'angular-oauth2-oidc';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';
import jwt_decode from 'jwt-decode';
import { MessageService } from 'primeng/api';
import { AppConfig } from '../app.config';
import { TclTokenClaims } from './token-claims.model';

@Injectable({
  providedIn: 'root'
})
export class OidcAuthService {

  // CAP-40007 TCL-2727 - Signals when loadDiscoveryDocumentAndLogin() has completed (success or failure).
  private isDoneLoadingSubject = new BehaviorSubject<boolean>(false);
  isDoneLoading$: Observable<boolean> = this.isDoneLoadingSubject.asObservable();

  // CAP-40007 TCL-2727 - Guard to prevent re-processing the same authorization code callback
  // This prevents redirect loops when the callback URL (code + state) remains in the browser history
  private callbackProcessed = false;

  // TCL-22-SECURITY - Set when the IDP redirects back with ?error=access_denied (user authenticated
  // but has no Service Applicative / role assigned). Consulted by AuthGuardService so it does
  // NOT redirect back to PingFederate (which would just reproduce the same denial in a loop).
  private accessDeniedSubject = new BehaviorSubject<boolean>(false);
  accessDenied$: Observable<boolean> = this.accessDeniedSubject.asObservable();

  // CAP-40007 TCL-2727 - Router is resolved lazily via Injector to avoid a circular dependency:
  // APP_INITIALIZER → OidcAuthService → Router is not yet available during APP_INITIALIZER.
  constructor(private oauthService: OAuthService, private injector: Injector) {}

  private get router(): Router {
    return this.injector.get(Router);
  }

  // TCL-SECURITY - Lazily resolved (same rationale as router: avoid a circular dependency
  // during APP_INITIALIZER). MessageService is only actually used from init() (Phase 2),
  // well after bootstrap, so this is safe.
  private get messageService(): MessageService {
    return this.injector.get(MessageService);
  }

  /**
   * CAP-40007 TCL-2727 - Phase 1: called from APP_INITIALIZER.
   * Only configure() + setStorage() so that OAuthService is ready before the router
   * activates any guard. Does NOT trigger discovery or login.
   */
  configure(): void {

    const cfg = AppConfig.settings;
    if (!cfg.clientID || !cfg.clientID.trim()) {
      throw new Error('OIDC configuration error: clientID is empty. Check config.json.');
    }
    const authConfig: AuthConfig = {
      issuer: cfg.issuerURL,
      clientId: cfg.clientID,
      redirectUri: cfg.redirectURI || window.location.origin,
      responseType: 'code',
      scope: cfg.scopeVar,
      logoutUrl: cfg.logoutURL,
      // TCL-22-SECURITY - NOT using AuthConfig.postLogoutRedirectUri here: cfg.logoutURL points
      // to PingFederate's classic SLO servlet (.../idp/startSLO.ping), which does NOT honor the
      // OIDC-standard `post_logout_redirect_uri` (+ `id_token_hint`) parameters that
      // angular-oauth2-oidc's logOut() builds. It only redirects back via its own
      // `TargetResource` query parameter - built manually in logout() below.
      requireHttps: cfg.requireHttps !== undefined ? cfg.requireHttps : true,
      strictDiscoveryDocumentValidation: false,
      showDebugInformation: false,
      // TCL-SECURITY - Clear the code+state from the URL after a successful exchange so a
      // manual page refresh does not re-submit the same authorization code.
      clearHashAfterLogin: true,
      // TCL-SECURITY - Disable session-check iframe: PingFederate may reject the iframe request.
      sessionChecksEnabled: false
    };
    this.oauthService.configure(authConfig);
    // CAP-40007 TCL-2727 - Keep tokens in sessionStorage (back-office, single-tab).
    this.oauthService.setStorage(sessionStorage);
  }

  /**
   * CAP-40007 TCL-2727 - Phase 2: called from AppComponent.ngOnInit.
   *
   * TOKEN RENEWAL MECHANISM (no refresh token):
   *   PingFederate does NOT return a refresh_token in the token response for this client.
   *   Token renewal relies on the PingFederate SSO session cookie (PF=...) that the IDP
   *   sets in the browser during the first login. When the access token expires (1 hour)
   *   and the user refreshes the page:
   *     1. hasValidAccessToken() = false (token expired in sessionStorage)
   *     2. initLoginFlow() redirects to PingFederate
   *     3. PF finds its valid session cookie → issues a new auth code INSTANTLY (no login screen)
   *     4. Browser returns with new ?code=...&state=...
   *     5. tryLogin() exchanges it → new access token stored in sessionStorage
   *   This full cycle is transparent to the user as long as the PF SSO session is alive.
   *
   *   NOTE: setupAutomaticSilentRefresh() is intentionally NOT called here.
   *   angular-oauth2-oidc v8 code-flow silent refresh requires either a refresh_token
   *   (not issued by PF for this client) or iframe-based prompt=none (blocked by PF's
   *   X-Frame-Options / CSP headers). Calling it only generates silent errors with no benefit.
   *
   * CORRECT OIDC REFRESH FLOW:
   *  Step A – Load discovery document (always needed for JWKS / token endpoints).
   *  Step B – Valid, non-expired token already in sessionStorage → reuse it. STOP.
   *           This is the normal page-refresh path when the token has not yet expired.
   *  Step C – No valid token → tryLogin() to exchange the authorization code in the URL
   *           (arriving from the IDP redirect with ?code=...&state=...).
   *  Step D – Still no token → initLoginFlow() to redirect to PingFederate.
   *           PF's SSO session cookie makes this transparent (no login screen).
   */
  init(): Promise<void> {
    const discoveryDocUrl = AppConfig.settings.issuerURL + '/.well-known/openid-configuration';
    

    // Step A – Load the discovery document so the library knows the token/authorize endpoints.
    return this.oauthService.loadDiscoveryDocument(discoveryDocUrl)
      .then(() => {

        // Step B – Token already in sessionStorage and not expired: reuse it.
        // This is the normal page-refresh path (token lifetime not yet reached).
        // No IDP redirect is necessary.
        // TCL-BUGFIX (routing) - Do NOT force-navigate to '/tcl' here. The Router already has a
        // guarded navigation in flight for the real browser URL (e.g. /tcl/saisie), waiting on
        // isDoneLoading$ via AuthGuardService. Calling router.navigate(['/tcl']) raced that
        // in-flight navigation and overrode it, causing a refresh on a child route (e.g.
        // /tcl/saisie) to bounce the URL back to /tcl, leave stale content displayed, drop the
        // active menu highlight, and break subsequent menu navigation. Simply unblocking
        // isDoneLoading$ below is sufficient to let the Router complete its own navigation.
        if (this.oauthService.hasValidAccessToken()) {
        
          // TCL-SECURITY - Reject the session if access_token/id_token identities don't match.
          this.assertTokenIdentityConsistency();
          this.callbackProcessed = true;
          this.isDoneLoadingSubject.next(true);
          return;
        }

        // Step C – No valid token in storage. Try to exchange an authorization code from the URL
        // (we may be arriving from the PingFederate redirect with ?code=...&state=...).
        // TCL-22-SECURITY - The IDP may instead redirect back with ?error=access_denied when the
        // user has no Service Applicative (SA) / role assigned. tryLogin() rejects with an
        // OAuthErrorEvent('code_error', ...) in that case - handled below via isAccessDeniedError().
 
        return this.oauthService.tryLogin().then(() => {
          if (this.oauthService.hasValidAccessToken()) {
          

            // TCL-SECURITY - Reject the session if access_token/id_token identities don't match.
            this.assertTokenIdentityConsistency();
            this.callbackProcessed = true;
            this.isDoneLoadingSubject.next(true);
            this.router.navigate(['/tcl']);
          } else {
            // Step D – No code in URL and no stored token: first visit or session cleared.
            // Redirect to PingFederate to start the Authorization Code + PKCE flow.
     
            this.oauthService.initLoginFlow();
          }
        }).catch(err => {
          // TCL-22-SECURITY - IDP returned ?error=access_denied: the user authenticated
          // successfully but holds no SA/role for this application. Show the dedicated
          // message and stop here - do NOT rethrow/redirect, to avoid an infinite
          // login <-> access_denied redirect loop (see AuthGuardService).
          if (this.isAccessDeniedError(err)) {
            this.handleAccessDenied(err);
            this.callbackProcessed = true;
            this.isDoneLoadingSubject.next(true);
            return;
          }
          throw err;
        });
      })
      .catch(err => {
  
        this.callbackProcessed = true;
        this.isDoneLoadingSubject.next(true);
        throw err;
      });
  }

  // CAP-40007 TCL-2727 - Start the interactive PingFederate login flow.
  login(): void {
 
    this.accessDeniedSubject.next(false); // TCL-SECURITY - clear any previous denial before a fresh attempt
    this.oauthService.initLoginFlow();
  }

  // CAP-40007 TCL-2727 - RP-initiated logout via PingFederate SLO + clear local tokens.
  // BUGFIX (400 Too Many Calls) - Reset callback flag on logout to allow re-authentication
  //
  // TCL-22-SECURITY - Do NOT use oauthService.logOut()'s own redirect: cfg.logoutURL is
  // PingFederate's classic SLO endpoint (.../idp/startSLO.ping), not an OIDC end_session_endpoint.
  // It ignores the library's `post_logout_redirect_uri` (+ `id_token_hint`) parameters and never
  // returns to the app - the browser is left stranded on PingFederate's own logout page.
  // PingFederate's SLO servlet instead honors its own `TargetResource` parameter for the
  // post-logout return URL, so that redirect is built and issued manually here.
  logout(): void {

    this.callbackProcessed = false; // Allow new login flow after logout
    this.accessDeniedSubject.next(false); // TCL-SECURITY - clear denial flag
    // Clear local tokens only - `true` (noRedirectToLogoutUrl) skips the library's own
    // (ineffective) redirect so we can issue the correct one below.
    this.oauthService.logOut(true);

    const cfg = AppConfig.settings;
    const returnUrl = cfg.redirectURI || window.location.origin;
    const separator = cfg.logoutURL.indexOf('?') > -1 ? '&' : '?';
    const logoutUrl = cfg.logoutURL + separator + 'TargetResource=' + encodeURIComponent(returnUrl);
 
    window.location.href = logoutUrl;
  }

  // CAP-40007 TCL-2727 - True only when a non-expired access token is present.
  get isAuthenticated(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  // TCL-22-SECURITY - True once the IDP has redirected back with ?error=access_denied
  // (user authenticated but has no SA/role assigned). Consulted by AuthGuardService.
  get isAccessDenied(): boolean {
    return this.accessDeniedSubject.getValue();
  }

  // CAP-40007 TCL-2727 - Bearer token attached to every API request by the interceptor.
  getAccessToken(): string {
    return this.oauthService.getAccessToken();
  }

  /**
   * TCL-SECURITY - Decode the access token and return the full claims as a typed TclTokenClaims
   * object. Returns null when no token is present or decoding fails.
   */
  getTokenClaims(): TclTokenClaims | null {
    return this.decodeAccessToken() as TclTokenClaims | null;
  }

  /**
   * CAP-40007 TCL-2727 - Username extracted from the access-token claims.
   * TCL-SECURITY - Prefers the short employee ID from 'username', falls back to 'upn'
   * (UPN contains the full domain login), then 'sub', then 'preferred_username'.
   */
  getUsername(): string | null {
    const claims = this.decodeAccessToken() as TclTokenClaims | null;
    if (!claims) {
      return null;
    }
    return (claims as any).username ||
           (claims as any).upn ||
           (claims as any).sub ||
           (claims as any).preferred_username ||
           null;
  }

  /**
   * CAP-40007 TCL-2727 / TCL-SECURITY - IDP role strings extracted from the access token
   * (e.g. ["ZTCL.AD.ADMIN", "ZTCL.AD.EXTRACTION"]).  These are the RAW IDP values –
  * AuthorizationService validates these values against IdpRoles before applying RolePolicy.
   */
  getRoles(): string[] {
    const claims = this.decodeAccessToken() as TclTokenClaims | null;
    if (!claims) {
    
      return [];
    }
    const raw: any = (claims as any).roles || (claims as any).groups || [];
    let roles: string[];
    if (Array.isArray(raw)) {
      roles = raw.map(String);
    } else if (typeof raw === 'string') {
      roles = raw.trim() ? raw.trim().split(/[\s,]+/) : [];
    } else {
      roles = [];
    }

    console.log(JSON.stringify({
      event: 'tcl.rbac.token-roles',
      message: 'Roles read from access token',
      roles
    }));

    return roles;
  }

  // CAP-40007 TCL-2727 - Decode the JWT access token payload (returns null on failure).
  private decodeAccessToken(): TclTokenClaims | null {
    const token = this.getAccessToken();
    if (!token) {
     
      return null;
    }
    try {
      const decoded = jwt_decode<TclTokenClaims>(token);
      return decoded;
    } catch (err) {
     
      return null;
    }
  }

  // TCL-SECURITY - Decode the ID token payload (returns null on failure).
  private decodeIdToken(): { sub?: string; [key: string]: any } | null {
    const idToken = this.oauthService.getIdToken();
    if (!idToken) {
     
      return null;
    }
    try {
      return jwt_decode<{ sub?: string; [key: string]: any }>(idToken);
    } catch (err) {
    
      return null;
    }
  }

  /**
   * TCL-SECURITY - Defense-in-depth check performed right after every token exchange/reuse:
   * when both tokens expose an identity, access_token.username MUST match id_token.sub.
   * Both tokens are issued together by PingFederate for the SAME authenticated user; a
   * mismatch means the access_token and id_token do not belong to the same identity
   * (e.g. token substitution/mixing) and the session must be rejected rather than trusted.
   *
   * On mismatch: revokes the local session, shows an "Access Denied" toast, and throws so
   * the caller's promise chain rejects (handled by OidcAuthService.init()'s existing .catch()).
   */
  private assertTokenIdentityConsistency(): void {
    const accessClaims = this.decodeAccessToken();
    const idClaims = this.decodeIdToken();
    const accessUsername = accessClaims ? (accessClaims as any).username : null;
    const idSubject = idClaims ? idClaims.sub : null;

    

    // PingFederate omits sub from some valid ID tokens. Without an ID-token identity there
    // is no second value to compare, so do not reject an otherwise valid refresh session.
    if (!idSubject) {
  
      return;
    }

    if (!accessUsername || accessUsername !== idSubject) {
    

      // Revoke the mismatched tokens locally. Deliberately NOT calling logOut() (which would
      // redirect through PingFederate SLO): with an active PF SSO session, an auto-redirect
      // back to login would likely just reproduce the same mismatch, causing a redirect loop.
      this.oauthService.logOut(true);

      this.messageService.add({
        severity: 'error',
        summary: 'Accès refusé',
        detail: 'Les jetons d\'authentification reçus sont incohérents. Veuillez réessayer ou contacter le support.',
        life: 8000
      });

      throw new Error('TCL-SECURITY: access denied - access_token/id_token identity mismatch (username=' + accessUsername + ', sub=' + idSubject + ')');
    }

  
  }

  /**
   * TCL-22-SECURITY - Detects the IDP's OAuth error redirect for a user who authenticated
   * successfully but holds no Service Applicative (SA) / role for this application.
   * angular-oauth2-oidc rejects tryLogin()'s promise with an OAuthErrorEvent('code_error', ...)
   * whose `params` carries the raw query-string parameters returned by PingFederate, e.g.
   * ?error=access_denied&error_description=...&state=...
   */
  private isAccessDeniedError(err: any): boolean {
    const params: any = err && (err as OAuthErrorEvent).params;
    return !!(params && params.error === 'access_denied');
  }

  /**
   * TCL-22-SECURITY - Handles the access_denied redirect from the IDP: marks the session as
   * denied (so AuthGuardService does not redirect back to PingFederate and cause a loop),
   * shows the required user-facing message for 5 seconds, then performs a silent logout.
   */
  private handleAccessDenied(err: any): void {
    const params: any = err && (err as OAuthErrorEvent).params;
  
    this.accessDeniedSubject.next(true);

    const TOAST_DURATION_MS = 5000;
    this.messageService.add({
      severity: 'error',
      summary: 'Access Denied',
      detail: 'You do not have a valid Service Applicative assigned.',
      life: TOAST_DURATION_MS
    });

    // TCL-22-SECURITY - Silent logout: once the toast has had time to display, automatically
    // log the user out (no click required) and return to the app - same sequencing used by
    // dmbr-allucare-ui for this scenario (see performSilentLogout()).
   
    setTimeout(() => {

      this.performSilentLogout();
    }, TOAST_DURATION_MS);
  }

  /**
   * TCL-22-SECURITY - Silent logout used specifically after an access_denied response, matching
   * the sequence used by dmbr-allucare-ui for the same IDP scenario:
   *   1. Clear the local session (no token was ever issued here, but reset flags/storage anyway).
   *   2. Navigate the browser to PingFederate's SLO endpoint (logoutURL) to end the SSO session.
   *   3. Do NOT wait on/trust PingFederate to redirect back on its own: cfg.logoutURL
   *      (.../idp/startSLO.ping) is PingFederate's proprietary SLO servlet, and when no
   *      token/session was ever established for this app (access was denied at /authorize,
   *      before any session existed) PingFederate has nothing to key a return-redirect on and
   *      may just show its own page without ever navigating back.
   *   4. Force the browser back to redirectURI a short moment later, guaranteeing the user
   *      always lands back on the app regardless of what PingFederate does.
   */
  private performSilentLogout(): void {
    this.callbackProcessed = false; // Allow a fresh login flow afterwards
    this.accessDeniedSubject.next(false); // Clear denial flag
    this.oauthService.logOut(true); // Clear local tokens only - no library navigation

    const cfg = AppConfig.settings;
    const returnUrl = cfg.redirectURI || window.location.origin;
    const RETURN_DELAY_MS = 2000;


    window.location.href = cfg.logoutURL;

    // TCL-22-SECURITY - Regardless of whether PingFederate navigates back on its own, force the
    // browser back to the app after a short delay so the user is never stranded on the IDP.
    setTimeout(() => {
      window.location.href = returnUrl;
    }, RETURN_DELAY_MS);
  }
}
